import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { Skeleton } from "@/ui/skeleton";
import {
  getPublicGalleryArchiveDownload,
  useInfinitePublicGalleryAssets,
  usePublicGallery,
  usePublicGalleryArchive,
  usePublicGalleryMutations,
  usePublicGallerySelection,
  type PublicGalleryScope,
} from "@/features/gallery/api";
import type { PublicGalleryAlbum, PublicGalleryAsset } from "@/features/gallery/types";
import { naturalCompare } from "@/features/gallery/natural-sort";
import { CustomerMasonry } from "./CustomerMasonry";
import { CustomerViewer } from "./CustomerViewer";
import { SelectionBar } from "./SelectionBar";
import { AlbumCover } from "./AlbumCover";
import {
  CustomerAlbumStrip,
  CustomerGalleryToolbar,
  GalleryStickyHeader,
  type GalleryFilter,
  type GallerySort,
  type SelectionStage,
} from "./CustomerGalleryControls";

// Màn album của KHÁCH — trình bày lại theo dự án PHOTO (CEO duyệt 3 điểm 27/08/2026):
//  1. Bìa trọn màn hình rồi trôi đi khi cuộn (không còn dải đen dính đỉnh màn).
//  2. MỘT thanh cuộn duy nhất — lưới nằm trong dòng chảy trang, ô đều 2:3 khe 2px.
//  3. Chọn ảnh bằng MỘT trái tim theo bước (dải Nháp → Cuối); nút chốt nổi đáy màn + hộp xác
//     nhận trước khi khoá.
// Chỉ đổi cách TRÌNH BÀY — mọi API/dữ liệu giữ nguyên như cũ.

const NO_PUBLIC_PERMISSIONS = { view: false, comment: false, select: false, download: false };

export function CustomerGalleryPage({ scope }: { scope: PublicGalleryScope }) {
  const gallery = usePublicGallery(scope);
  const assets = useInfinitePublicGalleryAssets(scope);
  const [albumId, setAlbumId] = useState("");
  const [viewerAsset, setViewerAsset] = useState<PublicGalleryAsset | null>(null);
  const [archiveId, setArchiveId] = useState("");
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<GalleryFilter>("all");
  // Mặc định theo TÊN TỆP máy ảnh (CEO chốt 13/10/2026): khách nói "tấm số 5" là thợ mở
  // đúng tấm, vì Kho ảnh cũng xếp theo tên.
  const [sort, setSort] = useState<GallerySort>("name-asc");
  const [stage, setStage] = useState<SelectionStage>("draft");

  // Thanh chọn ảnh nổi ở đáy màn CHỈ hiện khi khách đã cuộn qua bìa. Bìa kiểu gump đặt nút
  // "Xem album" ở góc dưới-trái, đúng chỗ thanh nổi ngồi — ở khổ điện thoại thanh che gần hết
  // nút. Ngoài ra khách còn đang ngắm bìa thì thanh "đã chọn 0 ảnh" cũng chưa có việc gì để làm.
  //
  // ⚠️ Hai cái bẫy đã cắn 27/08/2026, đừng lặp lại:
  // 1. Khối này phải nằm TRÊN mọi nhánh thoát sớm (đang tải / lỗi) — đặt xuống dưới là React nổ
  //    "Rendered more hooks than during the previous render" khi trang chuyển từ đang-tải sang
  //    có-dữ-liệu. Bài kiểm KHÔNG bắt được vì test luôn dựng sẵn dữ liệu, không đi qua nhánh đó.
  // 2. Phải là ref dạng GỌI LẠI, không phải useRef + useEffect([]): lúc mount trang còn đang tải,
  //    phần lưới chưa tồn tại nên effect chỉ chạy một lần với node rỗng rồi thôi.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [pastCover, setPastCover] = useState(false);
  const attachContent = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    // jsdom và trình duyệt cũ không có IntersectionObserver: thà luôn hiện thanh còn hơn khách
    // cuộn xuống lưới mà không có nút nào để chuyển vòng.
    if (typeof IntersectionObserver === "undefined") {
      setPastCover(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setPastCover(!!entry?.isIntersecting),
      // Cắt nửa dưới tầm nhìn ra khỏi vùng quan sát: lưới phải trồi lên quá giữa màn thì thanh
      // mới hiện, chứ vừa ló mép đáy màn đã hiện thì vẫn đè lên bìa.
      { rootMargin: "0px 0px -50% 0px" },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);
  const permissions = gallery.data?.permissions ?? NO_PUBLIC_PERMISSIONS;
  // Vì sao nút tải đang khoá — máy chủ nói, màn hình chỉ chép lại nguyên văn.
  // Tuyệt đối không tự đoán lý do ở đây: tiền là chuyện của máy chủ.
  const downloadLockReason = gallery.data?.downloadLockReason ?? "";
  const selection = usePublicGallerySelection(scope, permissions.select);
  const mutations = usePublicGalleryMutations(scope);
  const archive = usePublicGalleryArchive(scope, archiveId, !!archiveId);
  const allAssets = useMemo(
    () => assets.data?.pages.flatMap((page) => page.items) ?? [],
    [assets.data],
  );
  // Đếm, lọc, sắp đều tính trên ảnh ĐÃ tải về máy — nên tải đủ mọi trang (mỗi trang 100
  // tấm). Trước 13/10/2026 album 150 tấm vẫn ghi "Tất cả 100" và sắp tên chỉ trên 100
  // tấm đầu. Màn nhân viên làm y như vậy khi lọc/sắp.
  useEffect(() => {
    if (assets.hasNextPage && !assets.isFetchingNextPage && !assets.isError) {
      void assets.fetchNextPage();
    }
  }, [assets]);

  if (gallery.isLoading) {
    return <Skeleton className="mx-auto mt-8 h-[70dvh] w-[min(1100px,calc(100%-2rem))]" />;
  }
  if (gallery.error || !gallery.data) {
    return (
      <main className="mx-auto grid min-h-[70dvh] max-w-xl place-items-center p-6 text-center">
        <div className="space-y-3">
          <h1 className="text-xl font-semibold">Không mở được album</h1>
          <p className="text-sm text-muted-foreground">
            {(gallery.error as Error | null)?.message ?? "Album hiện không khả dụng."}
          </p>
          {scope.kind === "portal" ? (
            <Button asChild variant="outline">
              <a href="/booking/me">Đăng nhập lại</a>
            </Button>
          ) : null}
        </div>
      </main>
    );
  }

  const { root } = gallery.data;
  const albums = flattenAlbums(root);
  const effectiveAlbumId = albums.some((album) => album.id === albumId) ? albumId : root.id;
  const currentAlbum = albums.find((album) => album.id === effectiveAlbumId) ?? root;
  const albumAssets = uniqueAssets(
    effectiveAlbumId === root.id
      ? allAssets
      : allAssets.filter((item) => item.albumId === effectiveAlbumId),
  );
  const draftSelectedIds = new Set(
    (selection.data?.selections ?? [])
      .filter((item) => item.draftSelected)
      .map((item) => item.assetId),
  );
  const finalSelectedIds = new Set(
    (selection.data?.selections ?? [])
      .filter((item) => item.finalSelected)
      .map((item) => item.assetId),
  );
  const locked = !!selection.data?.lockedAt;
  const counts = getGalleryCounts(albumAssets, draftSelectedIds, finalSelectedIds);
  const visibleAssets = sortAssets(
    albumAssets.filter((item) => matchesFilter(item, filter, draftSelectedIds, finalSelectedIds)),
    sort,
  );
  const albumCounts = new Map(
    albums.map((album) => [
      album.id,
      album.id === root.id
        ? uniqueAssets(allAssets).length
        : uniqueAssets(allAssets.filter((item) => item.albumId === album.id)).length,
    ]),
  );
  const footerText = currentAlbum.footer.trim() || root.footer.trim();

  // Khách bấm tim / chốt mà máy chủ từ chối thì PHẢI nói (lỗi im lặng 24/09/2026):
  // tim không đổi màu mà không một chữ là khách tưởng máy đơ hoặc tưởng đã chọn.
  // Báo nổi lên trên cùng vì tim nằm giữa lưới dài hoặc trong khung xem toàn màn,
  // còn dòng báo lỗi của trang nằm tận cuối lưới.
  const reportFailure = (fallback: string) => (cause: unknown) => {
    toast.error(cause instanceof Error ? cause.message : fallback);
  };

  const toggleSelection = (asset: PublicGalleryAsset, selected: boolean) => {
    const mutation = stage === "final" ? mutations.selectFinal : mutations.select;
    void mutation
      .mutateAsync({ assetId: asset.assetId, selected })
      .catch(reportFailure("Chưa lưu được lựa chọn, bạn thử lại giúp Gạo Nâu nhé"));
  };

  // Mỗi vòng mở ra ĐÚNG tập mà vòng đó lọc từ đó (CEO chốt 27/08/2026).
  //
  // Vòng 1 lọc từ cả album, nên quay về vòng 1 là mở lại toàn album. Vòng 2 lọc tiếp từ tập vòng
  // 1, nên vào vòng 2 là lưới thu về đúng những tấm khách đã giữ — nếu không, khách vừa lọc cả
  // album xuống một tập gọn lại phải bơi giữa cả album lần nữa để mò ra tập của chính mình.
  // Chưa giữ tấm nào ở vòng 1 thì mở toàn album, kẻo vào vòng 2 gặp lưới trắng.
  const changeStage = (next: SelectionStage) => {
    setStage(next);
    setFilter(next === "final" && draftSelectedIds.size > 0 ? "draft" : "all");
  };

  const startDownload = (assetIds: string[]) => {
    setError("");
    void mutations.archive
      .mutateAsync(assetIds)
      .then((job) => setArchiveId(job.id))
      .catch((cause: Error) => setError(cause.message));
  };

  return (
    <main className={`h-[100dvh] overflow-y-auto bg-background text-foreground ${permissions.select ? "pb-8" : ""}`}>
      {/* Lề quanh bìa để dải bìa bo góc nổi lên khỏi nền trang, đúng ảnh mẫu CEO đưa 27/08/2026. */}
      <div className="p-3 sm:p-5">
        <AlbumCover
          scope={scope}
          album={currentAlbum}
          fallbackCoverAssetId={root.coverAssetId}
          assets={allAssets}
        />
      </div>
      <div ref={attachContent} id="gallery-content" className={permissions.select ? "pb-24" : ""}>
        <GalleryStickyHeader
          title={currentAlbum.title || currentAlbum.name}
          draftCount={draftSelectedIds.size}
          finalCount={finalSelectedIds.size}
          showCounter={permissions.select}
          stage={stage}
          showStage={permissions.select}
          locked={locked}
          onStageChange={changeStage}
          onCounterClick={() => setFilter(stage === "final" ? "selected-final" : "draft")}
        />
        <div className="mx-auto max-w-6xl space-y-4 px-4 py-4">
          <CustomerAlbumStrip
            albums={albums}
            activeAlbumId={effectiveAlbumId}
            counts={albumCounts}
            onAlbumChange={(nextAlbumId) => {
              setAlbumId(nextAlbumId);
              setFilter("all");
            }}
          />
          <CustomerGalleryToolbar
            filter={filter}
            counts={counts}
            sort={sort}
            stage={stage}
            canSelect={permissions.select}
            canDownload={permissions.download}
            downloadLockReason={downloadLockReason}
            downloadCounts={{
              all: albumCounts.get(root.id) ?? 0,
              draft: draftSelectedIds.size,
              final: finalSelectedIds.size,
            }}
            downloading={mutations.archive.isPending}
            onFilterChange={setFilter}
            onSortChange={setSort}
            onRefresh={() => void gallery.refetch()}
            onDownload={(downloadScope) => {
              if (downloadScope === "draft") return startDownload([...draftSelectedIds]);
              if (downloadScope === "final") return startDownload([...finalSelectedIds]);
              return startDownload([]);
            }}
          />
          {visibleAssets.length === 0 && albumAssets.length > 0 ? (
            <div className="flex min-h-[35vh] flex-col items-center justify-center gap-3 text-center">
              <p className="text-muted-foreground">Không có ảnh nào khớp với bộ lọc này.</p>
              <Button variant="outline" size="sm" onClick={() => setFilter("all")}>
                Xem tất cả ảnh
              </Button>
            </div>
          ) : (
            <CustomerMasonry
              scope={scope}
              items={visibleAssets}
              draftSelectedIds={draftSelectedIds}
              finalSelectedIds={finalSelectedIds}
              stage={stage}
              canSelect={permissions.select}
              locked={locked}
              onOpen={setViewerAsset}
              onToggle={toggleSelection}
              onNearEnd={() => {
                if (assets.hasNextPage && !assets.isFetchingNextPage) void assets.fetchNextPage();
              }}
              loadingMore={assets.isFetchingNextPage}
              hasMore={assets.hasNextPage}
            />
          )}
          {archiveId ? (
            <ArchiveProgress
              scope={scope}
              archiveId={archiveId}
              status={archive.data?.status}
              done={archive.data?.progressDone ?? 0}
              total={archive.data?.progressTotal ?? 0}
            />
          ) : null}
          {error ? (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
        <footer className="border-t px-4 py-10 text-center text-sm text-muted-foreground">
          {footerText ? <p className="whitespace-pre-line">{footerText}</p> : null}
          <p
            className="mt-2 text-base text-foreground"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Gạo Nâu Profile Studio
          </p>
        </footer>
      </div>
      {permissions.select && pastCover ? (
        <SelectionBar
          stage={stage}
          draftCount={draftSelectedIds.size}
          finalCount={finalSelectedIds.size}
          locked={locked}
          saving={mutations.commit.isPending}
          onStageChange={changeStage}
          // SelectionBar đóng hộp xác nhận CẢ KHI HỎNG — lý do phải nói ở đây, không
          // thì khách tưởng danh sách đã chốt.
          onCommit={() =>
            mutations.commit
              .mutateAsync()
              .then(
                () => undefined,
                reportFailure("Chưa chốt được danh sách, bạn thử lại giúp Gạo Nâu nhé"),
              )
          }
        />
      ) : null}
      <CustomerViewer
        scope={scope}
        assets={visibleAssets}
        asset={viewerAsset}
        canComment={permissions.comment}
        canDownload={permissions.download}
        downloadLockReason={downloadLockReason}
        canSelect={permissions.select}
        locked={locked}
        stage={stage}
        draftSelectedIds={draftSelectedIds}
        finalSelectedIds={finalSelectedIds}
        onAssetChange={setViewerAsset}
        onToggleDraft={(item, selected) =>
          void mutations.select
            .mutateAsync({ assetId: item.assetId, selected })
            .catch(reportFailure("Chưa lưu được lựa chọn, bạn thử lại giúp Gạo Nâu nhé"))
        }
        onToggleFinal={(item, selected) =>
          void mutations.selectFinal
            .mutateAsync({ assetId: item.assetId, selected })
            .catch(reportFailure("Chưa lưu được lựa chọn, bạn thử lại giúp Gạo Nâu nhé"))
        }
        onClose={() => setViewerAsset(null)}
      />
    </main>
  );
}

function ArchiveProgress({
  scope,
  archiveId,
  status,
  done,
  total,
}: {
  scope: PublicGalleryScope;
  archiveId: string;
  status: "queued" | "building" | "ready" | "failed" | "expired" | undefined;
  done: number;
  total: number;
}) {
  const [error, setError] = useState("");
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
      role="status"
    >
      <span>{status === "ready" ? "Album đã đóng gói" : "Đang đóng gói album"}</span>
      <strong>
        {done}/{total}
      </strong>
      {status === "ready" ? (
        <Button
          type="button"
          size="sm"
          onClick={async () => {
            try {
              const signed = await getPublicGalleryArchiveDownload(scope, archiveId);
              const anchor = document.createElement("a");
              anchor.href = signed.url;
              anchor.rel = "noopener";
              anchor.click();
            } catch (cause) {
              setError((cause as Error).message);
            }
          }}
        >
          <Download /> Tải file ZIP
        </Button>
      ) : null}
      {status === "failed" || status === "expired" ? (
        <span className="text-destructive">Tệp không còn khả dụng, vui lòng tạo lại.</span>
      ) : null}
      {error ? <span className="text-destructive">{error}</span> : null}
    </div>
  );
}

function flattenAlbums(root: PublicGalleryAlbum): PublicGalleryAlbum[] {
  return [root, ...root.children.flatMap(flattenAlbums)];
}

function uniqueAssets(assets: PublicGalleryAsset[]): PublicGalleryAsset[] {
  const ids = new Set<string>();
  return assets.filter((asset) => {
    if (ids.has(asset.assetId)) return false;
    ids.add(asset.assetId);
    return true;
  });
}

function getGalleryCounts(
  assets: PublicGalleryAsset[],
  draftSelectedIds: Set<string>,
  finalSelectedIds: Set<string>,
) {
  return {
    all: assets.length,
    recommended: assets.filter((asset) => asset.recommended).length,
    draft: assets.filter((asset) => draftSelectedIds.has(asset.assetId)).length,
    "selected-final": assets.filter((asset) => finalSelectedIds.has(asset.assetId)).length,
    final: assets.filter((asset) => asset.version.kind === "final").length,
  };
}

function matchesFilter(
  asset: PublicGalleryAsset,
  filter: GalleryFilter,
  draftSelectedIds: Set<string>,
  finalSelectedIds: Set<string>,
) {
  if (filter === "recommended") return asset.recommended;
  if (filter === "draft") return draftSelectedIds.has(asset.assetId);
  if (filter === "selected-final") return finalSelectedIds.has(asset.assetId);
  if (filter === "final") return asset.version.kind === "final";
  return true;
}

function sortAssets(assets: PublicGalleryAsset[], sort: GallerySort) {
  return [...assets].sort((left, right) => {
    // Sắp tên kiểu tự nhiên — IMG_2 trước IMG_10, cùng luật Kho ảnh và máy chủ.
    if (sort === "name-asc") return naturalCompare(left.originalFilename, right.originalFilename);
    if (sort === "name-desc") return naturalCompare(right.originalFilename, left.originalFilename);
    const direction = sort === "oldest" ? 1 : -1;
    return left.version.createdAt.localeCompare(right.version.createdAt) * direction;
  });
}
